import React, { useState } from "react";
import { AiOutlineSetting } from "react-icons/ai";
import { CurrentUserStocksQuery, useUnfollowStockMutation } from "../generated/graphql";
import {
  formatCurrentPrice,
  formatGain,
  formatLastComputedDates,
  formatLastSignalDate,
  formatLastSignalValue,
  formatRecommendation,
  formatTrend,
  isConsensusRecommendation,
  Recommendation,
  SignalMethod,
} from "../formatters/stock.formatters";
import StockSignalDrawer from "./StockSignalDrawer";

type StockItemProps = {
  stock: CurrentUserStocksQuery['currentUser']['stocks'][number];
};

const RECOMMENDATION_STYLES: Record<Recommendation, string> = {
  ACHETER: 'bg-green-100 text-green-800',
  VENDRE: 'bg-red-100 text-red-800',
  CONSERVER: 'bg-blue-100 text-blue-800',
  ATTENDRE: 'bg-gray-100 text-gray-500',
};

const CONSENSUS_STYLE = 'ring-2 ring-yellow-400 shadow-md';

const SIGNAL_LABELS: Record<SignalMethod, string> = {
  rsi: 'RSI',
  sma: 'SMA',
};

const StockItem: React.FC<StockItemProps> = ({ stock }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unfollowStock, { loading }] = useUnfollowStockMutation({
    variables: { stockId: stock.stock.id },
    refetchQueries: ['currentUserStocks', 'allStocks'],
  });

  const recommendationRsi = formatRecommendation(stock, 'rsi');
  const recommendationSma = formatRecommendation(stock, 'sma');
  const hasConsensus = isConsensusRecommendation(recommendationRsi, recommendationSma);
  const signalDate = formatLastSignalDate(stock);
  const signalValue = formatLastSignalValue(stock);
  const lastComputedDates = formatLastComputedDates(stock);
  const currentPrice = formatCurrentPrice(stock);
  const trend = formatTrend(stock);
  const trendColor = trend.startsWith('+') ? 'text-green-600' : trend.startsWith('-') ? 'text-red-600' : 'text-gray-400';
  const currentClose = stock.stock.historical2?.edges[0]?.node?.close;
  const gain = formatGain(stock, currentClose);
  const gainColor = gain.startsWith('+') ? 'text-green-600' : gain.startsWith('-') ? 'text-red-600' : 'text-gray-400';

  return (
    <>
    <tr className="border-b hover:bg-gray-50" data-testid={`stock-row-${stock.stock.id}`}>
      <td className="px-6 py-4">{stock.stock.name}</td>
      <td className="px-6 py-4">
        <div className="flex flex-col gap-1">
          {(['rsi', 'sma'] as const).map((method) => {
            const recommendation = method === 'rsi' ? recommendationRsi : recommendationSma;
            return (
              <span
                key={method}
                className={`inline-block px-2 py-1 rounded-sm text-xs font-semibold ${RECOMMENDATION_STYLES[recommendation]} ${hasConsensus ? CONSENSUS_STYLE : ''}`}
              >
                {SIGNAL_LABELS[method]}: {recommendation}
              </span>
            );
          })}
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm">{signalDate}</div>
        {signalValue !== '-' && (
          <div className="text-xs text-gray-500">{signalValue}</div>
        )}
      </td>
      <td className="px-6 py-4" data-testid={`stock-last-computed-${stock.stock.id}`}>
        {lastComputedDates.length > 0 ? (
          lastComputedDates.map(({ label, date }) => (
            <div key={label} className="text-xs text-gray-500">
              {label} : {date}
            </div>
          ))
        ) : (
          <span className="text-sm">-</span>
        )}
      </td>
      <td className="px-6 py-4">
        <div className="font-medium">{currentPrice}</div>
        {trend !== '-' && (
          <div className={`text-xs ${trendColor}`}>{trend}</div>
        )}
      </td>
      <td className="px-6 py-4">
        <span className={`font-medium ${gainColor}`}>{gain}</span>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid={`stock-settings-btn-${stock.stock.id}`}
            className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
            title="Configurer les signaux"
          >
            <AiOutlineSetting size={16} />
          </button>
          <button
            onClick={() => unfollowStock()}
            disabled={loading}
            data-testid={`stock-unfollow-btn-${stock.stock.id}`}
            className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50 focus:outline-hidden focus:ring-2 focus:ring-red-500 transition-colors"
          >
            Retirer
          </button>
        </div>
      </td>
    </tr>
    {drawerOpen && (
      <StockSignalDrawer
        stockId={stock.stock.id}
        stockName={stock.stock.name}
        stockSymbol={stock.stock.symbol}
        currentPrice={currentPrice}
        trend={trend}
        onClose={() => setDrawerOpen(false)}
      />
    )}
    </>
  );
};

export default StockItem;
